import { Link } from 'react-router-dom'
import type { LoanDetail } from '@/api/loans'
import { ROUTES } from '@/routes/routes'
import { useFormatCurrency, useListAmountFormat } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { CategoryIcon } from '@/lib/categoryIcon'
import { formatShortDate } from '@/lib/formatDate'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { emiRecordedInMonth } from './loanStats'

const ENTRY_NAMES = { EMI: 'EMI', PREPAYMENT: 'Prepayment', INCREASE: 'Increase' } as const

export function LoanIconChip({
  icon,
  repaid = false,
  className,
}: {
  icon: string
  repaid?: boolean
  className?: string
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid h-9 w-9 shrink-0 place-items-center rounded-[10px]',
        repaid
          ? 'bg-[var(--fl-income-bg)] text-[var(--fl-income)]'
          : 'bg-[var(--fl-expense-bg)] text-[var(--fl-expense)]',
        className
      )}
    >
      <CategoryIcon icon={icon} size={18} />
    </span>
  )
}

/** Principal and interest paid so far, as one split bar. */
export function PaidSplitBar({
  principal,
  interest,
  className,
}: {
  principal: number
  interest: number
  className?: string
}) {
  return (
    <div
      aria-hidden="true"
      className={cn('flex h-2 gap-0.5 overflow-hidden rounded bg-[var(--fl-divider)]', className)}
    >
      {principal > 0 && <div className="bg-[var(--fl-income)]" style={{ flex: principal }} />}
      {interest > 0 && <div className="bg-[var(--fl-warning-text)]" style={{ flex: interest }} />}
    </div>
  )
}

export function LoanCard({
  loan,
  icon,
  accountName,
  today,
  canPay,
  onPayEmi,
}: {
  loan: LoanDetail
  icon: string
  accountName: (id: string | null) => string | undefined
  today: Date
  canPay: boolean
  onPayEmi: () => void
}) {
  const { formatCompact } = useFormatCurrency()
  const format = useListAmountFormat()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const repaid = loan.outstanding <= 0
  const from = accountName(loan.emiAccountId)
  const last = loan.entries[0]
  const monthName = today.toLocaleDateString('en-IN', { month: wide ? 'long' : 'short' })
  // With no EMI set (interest-only, bullet) there's no monthly payment to expect.
  const expectsEmi = !repaid && loan.emiAmount != null
  const recorded = expectsEmi && emiRecordedInMonth(loan.entries, today)
  const paid = loan.principalRepaid + loan.interestPaid
  const showPay = canPay && expectsEmi && !recorded
  // On a phone the footer stays one line, so Pay EMI wins over the note.
  const footNote = wide
    ? last
      ? `Last: ${ENTRY_NAMES[last.type]} ${format(last.principalAmount + last.interestAmount)} · ${formatShortDate(last.date)}`
      : 'No payments yet'
    : showPay || paid <= 0
      ? null
      : `Paid ${format(paid)}`

  // A phone row has room for one line: the EMI matters more than the lender, who is usually in the name.
  const sub = [
    wide && loan.lender,
    loan.emiAmount != null
      ? `EMI ${format(loan.emiAmount)}${from ? ` from ${from}` : ''}`
      : 'No EMI set',
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <article className="relative flex min-w-0 flex-col gap-2.5 rounded-[14px] border border-[var(--fl-border)] bg-[var(--fl-surface)] px-3.5 py-3 transition-colors hover:border-[var(--fl-heading)] md:gap-3 md:p-4 md:px-[18px]">
      <div className="flex items-center gap-2.5 md:items-start md:gap-3">
        <LoanIconChip icon={icon} repaid={repaid} className="h-8 w-8 md:h-9 md:w-9" />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[14.5px] font-semibold leading-tight text-[var(--fl-heading)] md:whitespace-normal md:text-[15px]">
            {/* Stretched link: the whole card opens the loan, while Pay EMI stays its own button. */}
            <Link
              to={ROUTES.APP.LOAN_DETAIL(loan.id)}
              className="after:absolute after:inset-0 after:rounded-[14px] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-[var(--fl-primary)]"
            >
              {loan.name}
            </Link>
          </h3>
          <p className="mt-0.5 truncate text-xs text-[var(--fl-text-2)] md:whitespace-normal md:text-[12.5px]">
            {sub}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p
            className={cn(
              'whitespace-nowrap font-serif text-base font-bold leading-tight tabular-nums md:text-[22px]',
              repaid ? 'text-[var(--fl-income)]' : 'text-[var(--fl-heading)]'
            )}
          >
            {format(Math.max(loan.outstanding, 0))}
          </p>
          <p className="hidden text-[11.5px] text-[var(--fl-text-3)] md:block">
            {repaid ? 'repaid' : 'outstanding'}
          </p>
        </div>
      </div>

      {paid > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="hidden flex-wrap justify-between gap-2 text-xs text-[var(--fl-text-2)] md:flex">
            <span>
              Paid so far{' '}
              <span className="font-semibold text-[var(--fl-heading)]">{format(paid)}</span>
            </span>
            <span className="flex gap-3 text-[var(--fl-text-3)]">
              <span>{formatCompact(loan.principalRepaid)} principal</span>
              <span>{formatCompact(loan.interestPaid)} interest</span>
            </span>
          </div>
          <PaidSplitBar
            principal={loan.principalRepaid}
            interest={loan.interestPaid}
            className="h-1.5 md:h-2"
          />
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2.5 md:border-t md:border-[var(--fl-divider)] md:pt-2.5">
        {repaid ? (
          <span className="rounded-full bg-[var(--fl-primary-faint)] px-2.5 py-0.5 text-xs font-semibold text-[var(--fl-primary)]">
            ✓ Fully repaid
          </span>
        ) : expectsEmi ? (
          <span
            className={cn(
              'rounded-full px-2.5 py-0.5 text-xs font-semibold',
              recorded
                ? 'bg-[var(--fl-income-bg)] text-[var(--fl-income)]'
                : 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning)]'
            )}
          >
            {recorded ? `✓ ${monthName} EMI recorded` : `${monthName} EMI not recorded`}
          </span>
        ) : (
          <span />
        )}
        <span className="flex items-center gap-2.5">
          {footNote && <span className="text-xs text-[var(--fl-text-3)]">{footNote}</span>}
          {showPay && (
            <Button size="sm" onClick={onPayEmi} className="relative z-10">
              Pay EMI
            </Button>
          )}
        </span>
      </div>
    </article>
  )
}
